/**
 * Scheduled Reports Settings Page - Sprint 16
 * GC-8: Configure automated governance dashboard reports
 * 
 * Features:
 * - Create/edit scheduled report configurations
 * - Recipients management
 * - Cadence settings (weekly/monthly)
 * - Filter configuration
 * - Test delivery
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar, Clock, Mail, Plus, Edit2, Trash2, Loader2, RefreshCw,
  CheckCircle, AlertCircle, Send, Settings, Users, Filter, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }
  return headers;
};

// Day names for weekly schedule
const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' }
];

// Report Card Component
const ReportCard = ({ report, onEdit, onDelete, onTest, testing }) => {
  const nextRun = report.next_run ? new Date(report.next_run) : null;
  
  return (
    <div className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow" data-testid={`report-${report.report_id}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-gray-900">{report.name}</h3>
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              report.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {report.enabled ? 'Active' : 'Paused'}
            </span>
          </div>
          
          <div className="text-sm text-gray-500 space-y-1">
            <div className="flex items-center gap-2">
              <Calendar className="w-3 h-3" />
              <span>
                {report.frequency === 'weekly' 
                  ? `Weekly on ${DAYS_OF_WEEK.find(d => d.value === report.day_of_week)?.label || 'Monday'}`
                  : `Monthly on day ${report.day_of_month || 1}`
                }
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-3 h-3" />
              <span>{report.time_utc || '08:00'} UTC</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-3 h-3" />
              <span>{report.recipients?.length || 0} recipient(s)</span>
            </div>
            {nextRun && (
              <div className="flex items-center gap-2 text-blue-600">
                <Send className="w-3 h-3" />
                <span>Next: {nextRun.toLocaleDateString()} {nextRun.toLocaleTimeString()}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <button
            onClick={() => onTest(report.report_id)}
            disabled={testing}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-blue-600"
            title="Send Test"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onEdit(report)}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(report.report_id)}
            className="p-2 hover:bg-red-100 rounded-lg text-gray-500 hover:text-red-600"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Create/Edit Modal
const ReportModal = ({ isOpen, onClose, onSubmit, report, submitting }) => {
  const [form, setForm] = useState({
    name: '',
    report_type: 'governance_dashboard',
    frequency: 'weekly',
    day_of_week: 1,
    day_of_month: 1,
    time_utc: '08:00',
    recipients: [],
    branding: 'default',
    include_sections: ['kpis', 'charts', 'tables'],
    enabled: true
  });
  const [recipientInput, setRecipientInput] = useState('');
  
  useEffect(() => {
    if (report) {
      setForm({
        name: report.name || '',
        report_type: report.report_type || 'governance_dashboard',
        frequency: report.frequency || 'weekly',
        day_of_week: report.day_of_week ?? 1,
        day_of_month: report.day_of_month || 1,
        time_utc: report.time_utc || '08:00',
        recipients: report.recipients || [],
        branding: report.branding || 'default',
        include_sections: report.include_sections || ['kpis', 'charts', 'tables'],
        enabled: report.enabled ?? true
      });
    } else {
      setForm({
        name: '',
        report_type: 'governance_dashboard',
        frequency: 'weekly',
        day_of_week: 1,
        day_of_month: 1,
        time_utc: '08:00',
        recipients: [],
        branding: 'default',
        include_sections: ['kpis', 'charts', 'tables'],
        enabled: true
      });
    }
  }, [report]);
  
  const handleAddRecipient = () => {
    const email = recipientInput.trim();
    if (email && !form.recipients.includes(email)) {
      setForm({ ...form, recipients: [...form.recipients, email] });
      setRecipientInput('');
    }
  };
  
  const handleRemoveRecipient = (email) => {
    setForm({ ...form, recipients: form.recipients.filter(r => r !== email) });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Report name is required');
      return;
    }
    if (form.recipients.length === 0) {
      toast.error('At least one recipient is required');
      return;
    }
    onSubmit(form);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {report ? 'Edit Scheduled Report' : 'Create Scheduled Report'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="text-sm font-medium text-gray-700">Report Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Weekly Governance Report"
              className="w-full border rounded px-3 py-2 mt-1"
              required
            />
          </div>
          
          {/* Frequency */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Frequency</label>
              <select
                value={form.frequency}
                onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                className="w-full border rounded px-3 py-2 mt-1"
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            
            {form.frequency === 'weekly' ? (
              <div>
                <label className="text-sm font-medium text-gray-700">Day of Week</label>
                <select
                  value={form.day_of_week}
                  onChange={(e) => setForm({ ...form, day_of_week: parseInt(e.target.value) })}
                  className="w-full border rounded px-3 py-2 mt-1"
                >
                  {DAYS_OF_WEEK.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium text-gray-700">Day of Month</label>
                <select
                  value={form.day_of_month}
                  onChange={(e) => setForm({ ...form, day_of_month: parseInt(e.target.value) })}
                  className="w-full border rounded px-3 py-2 mt-1"
                >
                  {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          {/* Time */}
          <div>
            <label className="text-sm font-medium text-gray-700">Time (UTC)</label>
            <input
              type="time"
              value={form.time_utc}
              onChange={(e) => setForm({ ...form, time_utc: e.target.value })}
              className="w-full border rounded px-3 py-2 mt-1"
            />
          </div>
          
          {/* Recipients */}
          <div>
            <label className="text-sm font-medium text-gray-700">Recipients *</label>
            <div className="flex gap-2 mt-1">
              <input
                type="email"
                value={recipientInput}
                onChange={(e) => setRecipientInput(e.target.value)}
                placeholder="email@example.com"
                className="flex-1 border rounded px-3 py-2"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRecipient())}
              />
              <button
                type="button"
                onClick={handleAddRecipient}
                className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add
              </button>
            </div>
            
            {form.recipients.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {form.recipients.map(email => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemoveRecipient(email)}
                      className="text-gray-500 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          
          {/* Branding */}
          <div>
            <label className="text-sm font-medium text-gray-700">Branding</label>
            <div className="flex items-center gap-4 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="branding"
                  checked={form.branding === 'default'}
                  onChange={() => setForm({ ...form, branding: 'default' })}
                  className="text-blue-600"
                />
                <span className="text-sm">Include watermark</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="branding"
                  checked={form.branding === 'none'}
                  onChange={() => setForm({ ...form, branding: 'none' })}
                  className="text-blue-600"
                />
                <span className="text-sm">No branding</span>
              </label>
            </div>
          </div>
          
          {/* Enabled */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="enabled"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="enabled" className="text-sm text-gray-700">
              Enable scheduled delivery
            </label>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {report ? 'Save Changes' : 'Create Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Page Component
const ScheduledReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(null);
  
  // Load reports
  const loadReports = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/scheduled-reports`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || []);
      }
    } catch (err) {
      console.error('Failed to load reports:', err);
      toast.error('Failed to load scheduled reports');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadReports();
  }, [loadReports]);
  
  // Create/Update report
  const handleSubmit = async (form) => {
    setSubmitting(true);
    try {
      const url = editingReport
        ? `${API_BASE}/scheduled-reports/${editingReport.report_id}`
        : `${API_BASE}/scheduled-reports`;
      
      const method = editingReport ? 'PATCH' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });
      
      if (res.ok) {
        toast.success(editingReport ? 'Report updated' : 'Report created');
        setShowModal(false);
        setEditingReport(null);
        loadReports();
      } else {
        toast.error('Failed to save report');
      }
    } catch (err) {
      console.error('Failed to save report:', err);
      toast.error('Failed to save report');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Delete report
  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled report?')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_BASE}/scheduled-reports/${reportId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Report deleted');
        loadReports();
      } else {
        toast.error('Failed to delete report');
      }
    } catch (err) {
      toast.error('Failed to delete report');
    }
  };
  
  // Test report
  const handleTest = async (reportId) => {
    setTesting(reportId);
    try {
      const res = await fetch(`${API_BASE}/scheduled-reports/${reportId}/test`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.delivery_status === 'mock') {
          toast.info('Test sent (mock mode - SendGrid not configured)');
        } else {
          toast.success('Test report sent!');
        }
      } else {
        toast.error('Failed to send test');
      }
    } catch (err) {
      toast.error('Failed to send test');
    } finally {
      setTesting(null);
    }
  };
  
  // Edit report
  const handleEdit = (report) => {
    setEditingReport(report);
    setShowModal(true);
  };
  
  // Create new report
  const handleCreate = () => {
    setEditingReport(null);
    setShowModal(true);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100 p-6" data-testid="scheduled-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scheduled Reports</h1>
          <p className="text-gray-500">Configure automated governance dashboard reports</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadReports}
            className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-white"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="create-report-btn"
          >
            <Plus className="w-4 h-4" />
            New Report
          </button>
        </div>
      </div>
      
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-medium text-blue-800">Email Delivery Status</h3>
          <p className="text-sm text-blue-700">
            Email delivery is currently in <strong>mock mode</strong>. Reports will be generated but not actually sent until SendGrid API keys are configured.
          </p>
        </div>
      </div>
      
      {/* Reports List */}
      {reports.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-700">No Scheduled Reports</h3>
          <p className="text-gray-500 mt-1">Create your first scheduled report to automate governance dashboard delivery</p>
          <button
            onClick={handleCreate}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create Report
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {reports.map(report => (
            <ReportCard
              key={report.report_id}
              report={report}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onTest={handleTest}
              testing={testing === report.report_id}
            />
          ))}
        </div>
      )}
      
      {/* Create/Edit Modal */}
      <ReportModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingReport(null);
        }}
        onSubmit={handleSubmit}
        report={editingReport}
        submitting={submitting}
      />
    </div>
  );
};

export default ScheduledReportsPage;
